import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { trackToolUse } from '../utils/analytics';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Calculator, ArrowRight, MessageCircle, Loader2, Info, Zap, Globe } from 'lucide-react';
import Seo from '../components/Seo';
import { toolsAPI } from '../utils/api';
import { US_STATES, getStateRate } from '../data/usStates';
import { CALCULATOR_PAGES, getCalculatorPage } from '../data/calculatorPages';
import { STATE_LIST } from '../data/statePages';
import { REGION_LIST } from '../data/regionPages';
import ProductBridge from '../components/ProductBridge';
import { CALC_1099_CONTENT } from '../data/calc1099Content';
import PageGuide from '../components/PageGuide';
import ResultSignupCard from '../components/ResultSignupCard';

const DEFAULT_REGIONS = [
  { key: 'US', name: 'United States', flag: '🇺🇸', model: 'income_tax', currency: 'USD' },
  { key: 'UK', name: 'United Kingdom', flag: '🇬🇧', model: 'income_tax', currency: 'GBP' },
  { key: 'CA', name: 'Canada', flag: '🇨🇦', model: 'income_tax', currency: 'CAD' },
  { key: 'DE', name: 'Germany', flag: '🇩🇪', model: 'income_tax', currency: 'EUR' },
  { key: 'ES', name: 'Spain', flag: '🇪🇸', model: 'income_tax', currency: 'EUR' },
  { key: 'FR', name: 'France', flag: '🇫🇷', model: 'income_tax', currency: 'EUR' },
  { key: 'AU', name: 'Australia', flag: '🇦🇺', model: 'income_tax', currency: 'AUD' },
  { key: 'NZ', name: 'New Zealand', flag: '🇳🇿', model: 'income_tax', currency: 'NZD' },
  { key: 'KSA', name: 'Saudi Arabia', flag: '🇸🇦', model: 'vat', currency: 'SAR' },
  { key: 'UAE', name: 'United Arab Emirates', flag: '🇦🇪', model: 'vat', currency: 'AED' },
];

const SECONDARY_LABEL = { US: 'State tax rate (%)', CA: 'Provincial tax rate (%)' };

// Spanish translations for backend-provided US result labels + UI (embedded es calculator).
const ES_RESULT_LABELS = {
  'Estimated total tax': 'Impuesto total estimado',
  'Net profit': 'Ganancia neta',
  'Set aside each quarter': 'Aparta cada trimestre',
  'Self-employment tax (15.3%)': 'Impuesto por cuenta propia (15.3%)',
  'Federal income tax': 'Impuesto federal sobre la renta',
  'State tax': 'Impuesto estatal',
};

// Contextual internal links: each calculator points to its own region's ranking guide pages.
const RELATED_GUIDES = {
  US: [
    { to: '/1099-bookkeeping', label: '📊 1099 bookkeeping guide' },
    { to: '/schedule-c-receipt-scanner', label: '🧾 Schedule C receipt scanner' },
    { to: '/self-employment-tax-california', label: '🇺🇸 California' },
    { to: '/self-employment-tax-texas', label: '🇺🇸 Texas' },
    { to: '/self-employment-tax-new-york', label: '🇺🇸 New York' },
  ],
  UK: [
    { to: '/self-employed-tax-scotland', label: '🏴 Scotland tax guide' },
    { to: '/self-employed-tax-ireland', label: '🇮🇪 Ireland tax guide' },
  ],
  DE: [
    { to: '/freelancer-tax-germany', label: '🇩🇪 Germany freelancer tax guide' },
  ],
  ES: [
    { to: '/autonomo-tax-spain', label: '🇪🇸 Spain autónomo tax guide' },
  ],
  FR: [
    { to: '/auto-entrepreneur-tax-france', label: '🇫🇷 France auto-entrepreneur guide' },
  ],
  AU: [
    { to: '/self-employed-tax-australia', label: '🇦🇺 Australia sole trader tax guide' },
  ],
  NZ: [
    { to: '/self-employed-tax-new-zealand', label: '🇳🇿 New Zealand sole trader tax guide' },
  ],
};

// Country-specific calculator disclosures. Each mentions ONLY the assumptions relevant to
// that jurisdiction (no generic multi-country text) so it matches the actual calculation shown.
const DISCLOSURES = {
  US: 'Estimates use 2026 US federal figures (IRS Rev. Proc. 2025-32): self-employment tax of 15.3% (12.4% Social Security up to the $184,500 wage base + 2.9% Medicare) plus federal income tax on your net profit after the standard deduction. State and local taxes are only included if you set a state rate above. Simplified estimate for planning only — not tax advice.',
  UK: 'Estimates use 2025/26 UK figures (HMRC): the £12,570 Personal Allowance, Income Tax bands (20% / 40% / 45%) and Class 4 National Insurance (6% between £12,570 and £50,270, then 2%). Scottish income tax rates and Class 2 NI are not modelled. Simplified estimate for planning only — not tax advice.',
  CA: 'Estimates use 2026 CRA figures: the federal income-tax brackets, the $16,452 Basic Personal Amount, and CPP for the self-employed (11.9% base to the $74,600 YMPE, plus 8% CPP2 to the $85,000 YAMPE). Provincial/territorial tax is only included if you set a rate above. Simplified estimate for planning only — not tax advice.',
  DE: 'Estimates use the official 2026 German income-tax formula (§32a EStG) plus an estimate of public health (~14.6%) and long-term-care (~3.4%) contributions up to the annual ceiling. Actual social contributions for the self-employed vary; VAT (Umsatzsteuer) is separate. Simplified estimate for planning only — not tax advice.',
  ES: 'Estimates use 2026 Spanish figures: the aggregated IRPF income-tax scale and the RETA social-security minimum contribution base by income tier. Regional (CCAA) rates vary and IVA is separate. Simplified estimate for planning only — not tax advice.',
  FR: 'Estimates use 2026 French figures for micro-entrepreneurs: URSSAF social contributions and the CFP levy on turnover, plus income tax under the barème after the fixed abatement (single one-part household). TVA is separate. Simplified estimate for planning only — not tax advice.',
  AU: 'Estimates use the 2025-26 ATO resident income-tax brackets plus a simplified 2% Medicare levy above the low-income threshold. GST (10%) is separate. Simplified estimate for planning only — not tax advice.',
  NZ: "Estimates use the 2025-26 IRD income-tax brackets (no tax-free threshold) plus the ACC earners' levy (1.75% up to the maximum liable earnings). GST (15%) is separate. Simplified estimate for planning only — not tax advice.",
  UAE: 'Estimates your net UAE VAT at the standard 5% rate (VAT collected on sales minus VAT paid on expenses). Freelancers pay no personal income tax; check the AED 375,000 FTA registration threshold. Simplified estimate for planning only — not tax advice.',
  KSA: 'Estimates your net Saudi VAT at the standard 15% rate (VAT collected on sales minus VAT paid on expenses). Check the SAR 375,000 ZATCA registration threshold. Simplified estimate for planning only — not tax advice.',
};

// Spanish disclosure for the US (1099) Spanish calculator page.
const US_DISCLOSURE_ES = 'Los estimados usan las cifras federales de EE. UU. para 2026 (IRS Rev. Proc. 2025-32): el impuesto por cuenta propia del 15.3% (12.4% del Seguro Social hasta la base de $184,500 + 2.9% de Medicare) más el impuesto federal sobre la renta de tu ganancia neta tras la deducción estándar. Los impuestos estatales y locales solo se incluyen si defines una tasa estatal arriba. Estimado simplificado con fines de planeación — no es asesoría fiscal.';

export default function TaxCalculator({ initialRegion = 'US', embedded = false, calcNote = '', widget = false, lang = 'en' }) {
  const ES = lang === 'es';
  const tr = (s) => (ES ? (ES_RESULT_LABELS[s] || s) : s);
  const page = getCalculatorPage(initialRegion);
  const [regions, setRegions] = useState(DEFAULT_REGIONS);
  const navigate = useNavigate();
  const [regionKey, setRegionKey] = useState(initialRegion);
  const [income, setIncome] = useState('60000');
  const [expenses, setExpenses] = useState('8000');
  const [filing, setFiling] = useState('single');
  const [secondary, setSecondary] = useState('5');
  const [activity, setActivity] = useState('services_bnc');
  const [stateCode, setStateCode] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const loggedIn = !!localStorage.getItem('token') || !!localStorage.getItem('user');

  const region = regions.find((r) => r.key === regionKey) || DEFAULT_REGIONS[0];
  const isVat = region.model === 'vat';
  const isFR = region.key === 'FR';
  const LATIN_SYMBOLS = { USD: '$', GBP: '£', CAD: '$', EUR: '€', AUD: 'A$', NZD: 'NZ$' };
  const sym = LATIN_SYMBOLS[region.currency] || `${region.currency} `;
  const fmt = (n) => `${sym}${Math.round(Math.abs(n)).toLocaleString()}${n < 0 ? '' : ''}`;
  const fmtSigned = (n) => `${n < 0 ? '-' : ''}${sym}${Math.round(Math.abs(n)).toLocaleString()}`;

  useEffect(() => {
    toolsAPI.getRegions().then((res) => {
      if (res.data?.regions?.length) setRegions(res.data.regions);
    }).catch(() => {});
  }, []);

  const compute = useCallback(async () => {
    setLoading(true);
    try {
      const res = await toolsAPI.taxEstimate({
        region_key: regionKey,
        income: parseFloat(income) || 0,
        expenses: parseFloat(expenses) || 0,
        filing_status: filing,
        secondary_rate: parseFloat(secondary) || 0,
        activity,
      });
      setResult(res.data);
      trackToolUse('tax_calculator', { region: regionKey });
    } catch {
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [regionKey, income, expenses, filing, secondary, activity]);

  useEffect(() => {
    const t = setTimeout(compute, 300);
    return () => clearTimeout(t);
  }, [compute]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Self-Employment Tax Calculator (US, UK, Canada, UAE, Saudi)',
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Web',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    description: 'Free self-employment & freelancer tax calculator for the US (1099), UK, Canada, UAE and Saudi Arabia (VAT).',
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#D1FAE5]'} data-testid={embedded ? 'tax-calculator-widget' : 'tax-calculator-page'}>
      {!embedded && (
        <>
          <Seo
            title={page.seoTitle}
            description={page.seoDescription}
            path={page.path}
            jsonLd={jsonLd}
          />
          <header className="flex items-center justify-between px-4 sm:px-8 py-4 max-w-6xl mx-auto">
            <Link to={loggedIn ? '/dashboard' : '/'} className="flex items-center" data-testid="calc-logo-link">
              <img src="/logo-mark.png" alt="MoneyFeed" className="h-9 w-auto" />
            </Link>
            <div className="flex items-center gap-4">
              {regionKey === 'US' && (
                <Link to="/es/calculadora-impuestos-1099" data-testid="calc-es-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#064E3B] hover:text-[#84CC16]">
                  <Globe className="h-4 w-4" /> Español
                </Link>
              )}
              <Link to={loggedIn ? '/dashboard' : '/auth'}>
                <Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-5" data-testid="calc-signup-btn">
                  {loggedIn ? 'Back to app' : 'Track it free'}
                </Button>
              </Link>
            </div>
          </header>
        </>
      )}

      <div className={embedded ? '' : 'max-w-5xl mx-auto px-4 sm:px-8 pb-16'}>
        {!embedded && (
          <div className="text-center mb-8 mt-4">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white rounded-full shadow-sm mb-4">
              <Calculator className="h-4 w-4 text-[#064E3B]" />
              <span className="text-xs font-bold text-[#064E3B] uppercase tracking-wider">{page.badge}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#064E3B] mb-3">{page.h1}</h1>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">{page.intro}</p>
          </div>
        )}

        {/* Region selector (dropdown) — compute inline for supported regions, else open that country's guide */}
        <div className="flex justify-center mb-6" data-testid="calc-region-select-wrap">
          <div className="w-full max-w-xs text-center">
            <Label htmlFor="calc-region-select" className="text-xs font-bold text-[#064E3B] uppercase tracking-wider">{ES ? 'País / región' : 'Country / region'}</Label>
            <select
              id="calc-region-select"
              data-testid="calc-region-select"
              value={regionKey}
              onChange={(e) => {
                const v = e.target.value;
                if (v.startsWith('nav:')) { navigate(v.slice(4)); return; }
                setRegionKey(v);
              }}
              className="mt-1 w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-800 font-semibold"
            >
              <optgroup label={ES ? 'Calcular ahora' : 'Calculate now'}>
                {regions.map((r) => (
                  <option key={r.key} value={r.key}>{r.flag} {r.name}</option>
                ))}
              </optgroup>
              {!widget && !ES && (
                <>
                  <optgroup label="US states — guide">
                    {STATE_LIST.map((s) => (
                      <option key={s.slug} value={`nav:/${s.slug}`}>🇺🇸 {s.state}</option>
                    ))}
                  </optgroup>
                  <optgroup label="UK &amp; Europe — guide">
                    {REGION_LIST.map((r) => (
                      <option key={r.slug} value={`nav:/${r.slug}`}>{r.place === 'the Netherlands' ? 'Netherlands' : r.place}</option>
                    ))}
                  </optgroup>
                </>
              )}
            </select>
          </div>
        </div>

        {calcNote && (
          <p className="text-xs text-gray-500 text-center mb-4 max-w-xl mx-auto flex items-start gap-2 justify-center">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#064E3B]" /> {calcNote}
          </p>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6 bg-white shadow-lg">
            <div className="space-y-4">
              <div>
                <Label htmlFor="calc-income">{isVat ? 'Total revenue / sales' : (isFR ? 'Annual turnover (chiffre d’affaires)' : (ES ? 'Ingreso total' : 'Total income'))} ({sym}{ES ? '/año' : '/yr'})</Label>
                <Input id="calc-income" data-testid="calc-income" type="number" value={income} onChange={(e) => setIncome(e.target.value)} className="mt-2" />
              </div>
              {!isFR && (
                <div>
                  <Label htmlFor="calc-expenses">{ES ? 'Gastos del negocio' : 'Business expenses'} ({sym}{ES ? '/año' : '/yr'})</Label>
                  <Input id="calc-expenses" data-testid="calc-expenses" type="number" value={expenses} onChange={(e) => setExpenses(e.target.value)} className="mt-2" />
                </div>
              )}
              {isFR && (
                <div>
                  <Label htmlFor="calc-activity">Activity type</Label>
                  <select id="calc-activity" data-testid="calc-activity" value={activity} onChange={(e) => setActivity(e.target.value)}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800">
                    <option value="services_bnc">Liberal / freelance services (BNC)</option>
                    <option value="services_bic">Commercial / artisan services (BIC)</option>
                    <option value="goods">Sale of goods (BIC)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Micro-entrepreneur charges are on gross turnover — expenses aren't deductible.</p>
                </div>
              )}
              {region.key === 'US' && (
                <div>
                  <Label htmlFor="calc-filing">{ES ? 'Estado civil (para impuestos)' : 'Filing status'}</Label>
                  <select id="calc-filing" data-testid="calc-filing" value={filing} onChange={(e) => setFiling(e.target.value)}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800">
                    <option value="single">{ES ? 'Soltero(a)' : 'Single'}</option>
                    <option value="married_joint">{ES ? 'Casado(a), declaración conjunta' : 'Married filing jointly'}</option>
                    <option value="head_of_household">{ES ? 'Jefe(a) de familia' : 'Head of household'}</option>
                  </select>
                </div>
              )}
              {region.key === 'US' && (
                <div>
                  <Label htmlFor="calc-state">{ES ? 'Estado' : 'State'}</Label>
                  <select id="calc-state" data-testid="calc-state" value={stateCode}
                    onChange={(e) => { setStateCode(e.target.value); const r = getStateRate(e.target.value); if (r != null) setSecondary(String(r)); }}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800">
                    {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                  </select>
                </div>
              )}
              {SECONDARY_LABEL[region.key] && (
                <div>
                  <Label htmlFor="calc-secondary">{ES ? 'Tasa de impuesto estatal (%)' : SECONDARY_LABEL[region.key]}{region.key === 'US' ? (stateCode ? (ES ? ' (autocompletado, editable)' : ' (auto-filled, editable)') : '') : (ES ? ' — opcional' : ' — optional')}</Label>
                  <Input id="calc-secondary" data-testid="calc-secondary" type="number" value={secondary} onChange={(e) => setSecondary(e.target.value)} className="mt-2" />
                  {region.key === 'US' && <p className="text-xs text-gray-500 mt-1">{ES ? 'Los estados sin impuesto sobre la renta quedan en 0%. Es un estimado — ajústalo por impuestos locales.' : 'States with no income tax are set to 0%. Estimate only — adjust for local taxes.'}</p>}
                </div>
              )}
              {isVat && (
                <p className="text-xs text-gray-500 flex items-start gap-2"><Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#064E3B]" /> {region.name} freelancers pay no personal income tax — this estimates your net {region.currency === 'SAR' ? 'VAT' : 'VAT'} payable (VAT collected on sales minus VAT paid on expenses).</p>
              )}
            </div>
          </Card>

        <div className="flex flex-col gap-6">
            <Card className="p-6 bg-[#064E3B] text-white shadow-lg flex flex-col">
              {loading || !result ? (
                <div className="flex-1 flex items-center justify-center min-h-[280px]"><Loader2 className="h-8 w-8 animate-spin text-[#84CC16]" /></div>
              ) : (
                <>
                  <p className="text-[#A7F3D0] text-sm mb-1">{tr(result.total_label)}</p>
                  <p className="text-5xl font-bold mb-1" data-testid="calc-total">{fmt(result.total)}</p>
                  <p className="text-[#A7F3D0] text-sm mb-6">{ES ? `Tasa efectiva ${(result.effective_rate * 100).toFixed(1)}% sobre ${fmt(result.primary_value)} de ${tr(result.primary_label).toLowerCase()}` : `Effective rate ${(result.effective_rate * 100).toFixed(1)}% on ${fmt(result.primary_value)} ${result.primary_label.toLowerCase()}`}</p>
  
                  <div className="space-y-3 flex-1">
                    {result.lines.map((l, i) => (
                      <Row key={i} label={tr(l.label)} value={fmtSigned(l.amount)} />
                    ))}
                    <div className="border-t border-white/20 pt-3">
                      <Row label={tr(result.quarterly_label)} value={fmt(result.quarterly)} bold />
                    </div>
                    {result.note && (
                      <p className="text-xs text-[#A7F3D0] pt-2" data-testid="calc-vat-note">{result.note}</p>
                    )}
                  </div>
  
                  <div className="mt-6 bg-white/10 rounded-xl p-4">
                    <p className="text-sm text-[#D1FAE5] mb-3">{ES ? `Deja de adivinar en la temporada de impuestos. MoneyFeed registra cada gasto desde WhatsApp y arma esto automáticamente para ${result.region_name}.` : `Stop guessing at tax time. MoneyFeed tracks every expense from WhatsApp and builds this automatically for ${result.region_name}.`}</p>
                    <Link to="/auth">
                      <Button className="w-full bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full" data-testid="calc-cta-btn">
                        {ES ? 'Empieza gratis' : 'Start tracking free'} <ArrowRight className="h-4 w-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </>
              )}
              </Card>
          {result && !loading && (
            <ResultSignupCard resultTotal={fmt(result.total)} />
          )}
        </div>
        </div>

        <p className="text-xs text-gray-500 text-center mt-6 max-w-2xl mx-auto" data-testid="calc-disclosure">
          {ES ? US_DISCLOSURE_ES : (DISCLOSURES[regionKey] || DISCLOSURES.US)}
        </p>

        {widget && (
          <div className="text-center mt-6 pt-4 border-t border-gray-200">
            <a
              href="/"
              target="_blank"
              rel="noopener"
              data-testid="widget-backlink"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#064E3B] hover:text-[#84CC16] transition-colors"
            >
              <Zap className="h-4 w-4" /> Powered by MoneyFeed — free freelancer &amp; self-employed tax calculator
            </a>
          </div>
        )}

        {!embedded && <PageGuide />}

        {!embedded && regionKey === 'US' && (
          <section className="mt-10 pt-8 border-t border-gray-200 max-w-3xl mx-auto text-left" data-testid="calc-1099-content">
            {CALC_1099_CONTENT.sections.map((s) => (
              <div key={s.i} className="mb-5">
                <h2 className="text-lg font-bold text-[#064E3B] mb-2">{s.h}</h2>
                <p className="text-sm text-gray-700 leading-relaxed">{CALC_1099_CONTENT.paragraphs[s.i]}</p>
              </div>
            ))}
            <h2 className="text-xl font-bold text-[#064E3B] mt-8 mb-3">Frequently asked questions</h2>
            <div className="space-y-4">
              {CALC_1099_CONTENT.faqs.map((f, idx) => (
                <div key={idx} data-testid={`calc-1099-faq-${idx}`}>
                  <h3 className="text-sm font-semibold text-gray-900">{f.q}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {!embedded && initialRegion === "DE" && (<section className="mt-10 pt-8 border-t border-gray-200 max-w-3xl mx-auto text-left"><h2 className="text-xl font-bold text-[#064E3B] mt-8 mb-3">Frequently asked questions</h2><div className="space-y-4"><div><h3 className="text-sm font-semibold text-gray-900">What does the Germany calculator estimate?</h3><p className="text-sm text-gray-700 leading-relaxed">It estimates German income tax (Einkommensteuer, 2026 §32a), health and long-term care contributions, plus a 19% VAT (Umsatzsteuer) summary with the Kleinunternehmer limit.</p></div><div><h3 className="text-sm font-semibold text-gray-900">How are health and care contributions estimated?</h3><p className="text-sm text-gray-700 leading-relaxed">Estimates use public health (about 14.6%) and long-term care (about 3.4%) contributions up to the annual ceiling. Actual social contributions for the self-employed vary.</p></div><div><h3 className="text-sm font-semibold text-gray-900">Is VAT included, and is this tax advice?</h3><p className="text-sm text-gray-700 leading-relaxed">No — VAT (Umsatzsteuer) is separate, and this is a simplified estimate for planning only, not tax advice.</p></div></div></section>)} {!embedded && <ProductBridge intent="tax" className="mt-10" />} {!embedded && (initialRegion === "US" || initialRegion === "DE") && (<section className="max-w-4xl mx-auto px-4 sm:px-8 pb-8"><div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center"><p className="text-sm font-semibold text-[#064E3B]">Track expenses automatically</p><p className="text-lg font-bold text-gray-900 mt-1">Meet MoneyFeed Scan Center</p><p className="text-gray-600 text-sm mt-2">Snap or send receipts on WhatsApp or in the app and MoneyFeed reads, categorizes and files them into clean books.</p><div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-4"><Link to="/scan-center" className="inline-block text-sm font-semibold text-[#0D9488]">Explore Scan Center →</Link><Link to="/auth" className="inline-block text-sm font-semibold text-[#064E3B]">Get started free →</Link></div></div></section>)}

        {!embedded && (
          <div className="mt-10 pt-8 border-t border-gray-200">
            <p className="text-center text-sm font-bold text-[#064E3B] uppercase tracking-wider mb-4">Calculators by country</p>
            <div className="flex flex-wrap justify-center gap-2" data-testid="calc-country-links">
              {CALCULATOR_PAGES.map((c) => (
                <Link key={c.path} to={c.path} data-testid={`calc-country-link-${c.regionKey}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${c.regionKey === regionKey ? 'bg-[#064E3B] text-white border-[#064E3B]' : 'bg-white text-[#064E3B] border-gray-200 hover:border-[#84CC16]'}`}>
                  <span className="mr-1">{c.flag}</span>{c.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        {!embedded && RELATED_GUIDES[regionKey] && (
          <div className="mt-8" data-testid="calc-related-guides">
            <p className="text-center text-sm font-bold text-[#064E3B] uppercase tracking-wider mb-4">Guides for your region</p>
            <div className="flex flex-wrap justify-center gap-2">
              {RELATED_GUIDES[regionKey].map((g) => (
                <Link key={g.to} to={g.to} data-testid={`calc-related-guide-${g.to}`}
                  className="px-4 py-2 rounded-full text-sm font-semibold border bg-white text-[#064E3B] border-gray-200 hover:border-[#84CC16] transition-colors">
                  {g.label}
                </Link>
              ))}
            </div>
          </div>
        )}

        {!embedded && (
          <div className="text-center mt-10">
            <Link to="/blog" className="inline-flex items-center gap-2 text-[#064E3B] font-semibold hover:underline" data-testid="calc-blog-link">
              <MessageCircle className="h-4 w-4" /> Read our freelancer tax guides
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value, bold }) => (
  <div className="flex justify-between items-center">
    <span className={`text-sm ${bold ? 'font-bold text-white' : 'text-[#D1FAE5]'}`}>{label}</span>
    <span className={`${bold ? 'text-xl font-bold' : 'text-base font-semibold'} text-white`}>{value}</span>
  </div>
);
